<template>
  <div id="messages-container" class="d-flex flex-column">
    <template v-for="(item, index) in messages" :key="index">
      <AIAssistantMessagesItem :item="item" :is-last-message="index === messages.length - 1" @retry="$emit('retry', index)" />
    </template>
    <template v-if="postPromptPending">
      <AppChatMessage ref="chatMessage" class="pb-6">
        <template #message-author>
          <app-icon-ai-assistant :width="32" :height="32" />
        </template>
        <div class="d-flex flex-column row-gap-4">
          <div>
            <span class="font-weight-bold">{{ $t(`${TRANSLATION_KEY_PREFIX}.postPromptPending`) }}</span>
          </div>
          <hr />
          <template v-for="(step, index) in $tm(`${TRANSLATION_KEY_PREFIX}.postPromptPendingSteps`)" :key="index">
            <div v-if="Number(index) < visibleStepsCount" class="d-flex column-gap-2">
              <app-material-icon
                :name="Number(index) < completedStepsCount ? 'check' : 'progress_activity'"
                :class="Number(index) < completedStepsCount ? 'text-success' : 'fa-spin text-purple-darken-1'"
              />
              <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.postPromptPendingSteps.${index}`) }}</span>
            </div>
          </template>
        </div>
      </AppChatMessage>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import AIAssistantMessagesItem from '@/common/ai-assistant/AIAssistantMessagesItem.vue'
import AppChatMessage from '@/common/app-chat/AppChatMessage.vue'
import type { AIAssistantConversationMessage } from '@/types/ai-assistant'

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantMessages'

export default defineComponent({
  name: 'AIAssistantMessages',
  emits: ['retry'],
  components: {
    AppChatMessage,
    AIAssistantMessagesItem,
  },
  props: {
    messages: {
      type: Array as PropType<AIAssistantConversationMessage[]>,
      required: true,
    },
    postPromptPending: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      TRANSLATION_KEY_PREFIX,
      completedStepsCount: 0,
      timeouts: [] as ReturnType<typeof setTimeout>[],
      visibleStepsCount: 1,
    }
  },
  mounted() {
    if (this.postPromptPending) {
      this.startStepsProgression()
    }
  },
  watch: {
    postPromptPending(newVal) {
      if (newVal) {
        this.resetAndStartProgression()
      } else {
        this.clearTimeouts()
      }
    }
  },
  methods: {
    resetAndStartProgression() {
      this.clearTimeouts()

      this.visibleStepsCount = 1
      this.completedStepsCount = 0

      this.startStepsProgression()
    },

    clearTimeouts() {
      for (const timeout of this.timeouts) {
        clearTimeout(timeout)
      }
      this.timeouts = []
    },

    scrollToLatestStep() {
      this.$nextTick(() => {
        const chatMessageElement = this.$refs['chatMessage'] as any
        if (chatMessageElement && chatMessageElement.$el) {
          const element = chatMessageElement.$el as HTMLElement
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          })
        }
      })
    },

    startStepsProgression() {
      this.timeouts.push(
        setTimeout(() => {
          this.completedStepsCount = 1
        }, 6000),

        setTimeout(() => {
          this.visibleStepsCount = 2
          this.scrollToLatestStep()
        }, 6100),

        setTimeout(() => {
          this.completedStepsCount = 2
        }, 12000),

        setTimeout(() => {
          this.visibleStepsCount = 3
          this.scrollToLatestStep()
        }, 12100)
      )
    }
  },
  beforeUnmount() {
    this.clearTimeouts()
  },
})
</script>

<style scoped lang="scss">
hr {
  border: 0;
  border-top: 1px solid #C5CED9;
  margin: 0 !important;
}
</style>
